/**
 * Flocks that scatter when approached (lifted from the shared prototype
 * engine's `engine/src/birds.js`, as lantern-water's `main.js` uses it): every
 * bird is two quads (a blob shadow and a camera-facing sprite card) in one
 * immutable buffer and one draw. The vertex shader evaluates the idle pecking
 * and hopping, the staggered takeoff, the flight and the landing from `time`
 * and one vec4 per flock (takeoff time, landing time, flee direction); the
 * CPU does one distance check per flock and actor a frame. No collision, no
 * navigation.
 *
 * The flocks are the scene's (`birds` in the record: ground flocks scattered
 * around a point, perched lines along a rail, or explicit perch spots), in
 * game axes; this module works in three.js axes (x, h, −y) like the
 * prototype, so the scatter and the flight paths are the prototype's. The
 * actors are the traveler and the walking residents (a resident only flushes
 * them when it passes right by: `sceneLook.birds.residentRadius`). A ground
 * flock leaves at dusk and roosts out of sight; a perched one stays.
 *
 * Medium only (the Rendering page): Off draws none, and frees the buffer, the
 * material's program and the atlas. Under reduced motion (`enabled` false)
 * every bird stays home in its still idle pose, as the prototype's.
 */
import type { Scene } from '@ab/content-schema';
import * as THREE from 'three';
import { seededRandom } from '../grid.ts';
import { addShaderHook } from '../material-hooks.ts';
import { birdAtlas, birdCell, birdFeetRow, birdFrames, birdPixel, birdRows } from './bird-art.ts';
import { sceneLook } from './constants.ts';

type Flock = Scene['birds'][number];

/** Something that scares a flock: where it stands (three.js x, z) and, for a resident, its own reach. */
export type BirdActor = { readonly x: number; readonly z: number; readonly radius?: number };

export type FlockStatus = 'grounded' | 'flying' | 'landing' | 'away' | 'roosting';

/** A flock's state on the CPU (the shader reads its takeoff, landing and flee direction). */
type FlockState = {
  readonly id: string;
  readonly index: number;
  readonly x: number;
  readonly z: number;
  readonly radius: number;
  readonly roosts: boolean;
  takeoffs: number;
  takeoff: number;
  landing: number;
  quietSince: number;
  readonly flee: THREE.Vector2;
  status: FlockStatus;
};

export type Birds = {
  readonly mesh: THREE.Mesh;
  /** Birds in the buffer (drawn while active). */
  readonly count: number;
  setActive(active: boolean): void;
  update(dt: number, actors: readonly BirdActor[], options: { readonly enabled: boolean; readonly night: number }): void;
  /** Each flock's state now (tests and the stats). */
  flocks(): readonly { readonly id: string; readonly status: FlockStatus }[];
  /** Frees the buffer's, the material's and the atlas's GPU copies (Off). */
  release(): void;
  dispose(): void;
};

const maxFlocks = 8;
const b = sceneLook.birds;

const birdCommon = (cell: number): string => `
#define FRAMES ${birdFrames.length}.
#define ROWS ${birdRows.length}.
attribute vec3 corner; attribute vec4 seed; attribute vec4 info;
uniform float time, reduced, lowQuality; uniform vec4 flocks[${maxFlocks}];
varying vec2 vBirdUv; varying float vBirdKind, vBirdAlpha;
vec3 bTransformed; vec2 bOffset; vec2 bUv;
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
vec2 wander(float k, float perch){
  vec2 h = vec2(hash(vec2(k, seed.x * 91.)), hash(vec2(seed.y * 57., k))) - .5;
  return perch > .5 ? vec2(h.x * .16, 0.) : h * vec2(.55, .38);
}
vec3 flight(float u, vec2 dir){
  float along = 24. + seed.y * 9., up = 10. + seed.x * 5.;
  vec2 side = vec2(-dir.y, dir.x);
  float s = along * pow(u, 2.2);
  float h = up * (1. - pow(1. - u, 3.4));
  float sway = sin(u * 5.5 + seed.z * 6.28) * .9 * u;
  return vec3(dir * s + side * sway, h).xzy;
}
void birdEval(){
  vec4 F = flocks[int(info.x + .5)];
  float gull = info.w, perch = info.y;
  float delay = seed.z * (gull > .5 ? .55 : .38) + seed.x * .08;
  float dur = (gull > .5 ? 4.2 : 3.) + seed.y * .9;
  float u;
  bool flying = F.x > F.y;
  if (flying) {
    float u0 = 1. - clamp((F.x - F.y - delay * .6) / (dur * 1.25), 0., 1.);
    u = clamp(u0 + (time - F.x - delay * step(u0, 0.)) / dur, 0., 1.);
  } else {
    u = 1. - clamp((time - F.y - delay * .6) / (dur * 1.25), 0., 1.);
  }
  if (reduced > .5) u = u >= 1. ? 1. : 0.;
  float jitter = (seed.w + seed.x - .5) * .9;
  vec2 dir = normalize(F.zw + vec2(1e-4));
  float ca = cos(jitter), sa = sin(jitter);
  dir = vec2(ca * dir.x - sa * dir.y, sa * dir.x + ca * dir.y);
  float period = 3.4 + seed.y * 2.8;
  float ct = time / period + seed.x * 17.;
  float k = floor(ct), c = fract(ct);
  vec2 from = wander(k - 1., perch), to = wander(k, perch);
  float move = smoothstep(0., .16, c);
  vec2 off = mix(from, to, move);
  float action = hash(vec2(k, seed.z * 13.));
  float lift = 0.;
  float frame = 0.;
  vec3 camRight = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
  float face = dot(vec3(to.x - from.x, 0., to.y - from.y), camRight);
  face = abs(face) < .02 ? (hash(vec2(k, seed.y)) > .5 ? 1. : -1.) : sign(face);
  if (c < .16 && perch < .5) {
    frame = gull > .5 ? 0. : (fract(c * 24.) < .5 ? 0. : 1.);
    if (action > .7) { frame = 4.; lift = sin(c / .16 * 3.14159) * .07; }
  } else if (action < .5) {
    float pc = (c - .22) / .45;
    if (pc > 0. && pc < 1.) {
      float beat = fract(pc * 5.);
      frame = beat < .45 ? 3. : beat < .7 ? 2. : 0.;
    } else frame = c > .8 ? 1. : 0.;
  } else if (action < .8) {
    frame = fract(c * 3.) < .4 ? 1. : 0.;
    if (c > .55) face = -face;
  } else if (action < .9) {
    float fc = (c - .3) / .22;
    if (fc > 0. && fc < 1.) {
      frame = 5. + mod(floor(fc * 10.), 3.);
      lift = sin(fc * 3.14159) * (perch > .5 ? .05 : .16);
    } else frame = 0.;
  } else frame = c > .5 ? 1. : 0.;
  vec3 p = position + vec3(off.x, lift, off.y);
  float alive = 1.;
  if (u > 0.) {
    vec3 a = flight(u, dir), b = flight(min(u + .02, 1.), dir) - flight(max(u - .02, 0.), dir);
    p = position + vec3(off.x, 0., off.y) + a;
    face = dot(b, camRight) >= 0. ? 1. : -1.;
    float wing = floor(fract(time * (gull > .5 ? 2.2 : 3.2) + seed.x) * 4.);
    float flap = wing == 0. ? 5. : wing == 2. ? 7. : 6.;
    bool gliding = u > .45 && fract(time * .45 + seed.y) > .55;
    if (!flying) gliding = u > .22 || fract(time * .8 + seed.x) > .6;
    frame = gliding ? 8. : flap;
    alive = 1. - step(.999, u);
  } else if (flying && time > F.x) {
    frame = 1.;
  }
  if (reduced > .5) { frame = 0.; p = position; }
  alive *= info.z > .5 || lowQuality < .5 ? 1. : 0.;
  vBirdKind = corner.z;
  float height = p.y - position.y;
  if (corner.z < .5) {
    bTransformed = vec3(p.x, position.y + .03, p.z) + vec3(corner.x * (gull > .5 ? .24 : .17), 0., corner.y * (gull > .5 ? .11 : .08));
    vBirdUv = corner.xy; bUv = vec2(0.); bOffset = vec2(0.);
    vBirdAlpha = (1. - smoothstep(0., 1.4, height)) * (1. - perch) * alive;
  } else {
    float cell = ${cell.toFixed(5)};
    float feet = ${((birdCell - birdFeetRow - 1) / birdCell).toFixed(5)};
    bTransformed = p;
    bOffset = vec2(corner.x * .5, corner.y * .5 + .5 - feet) * cell * alive;
    vec2 uv = corner.xy * .5 + .5;
    if (face < 0.) uv.x = 1. - uv.x;
    bUv = vec2((frame + uv.x) / FRAMES, 1. - (seed.w + 1. - uv.y) / ROWS);
    vBirdUv = uv; vBirdAlpha = alive;
  }
}
`;

/** The flocks of a scene record, built at their places (`groundAt`: the walkable height under three.js x, z). */
export const createBirds = (flocks: Scene['birds'], groundAt: (x: number, z: number) => number): Birds => {
  const random = seededRandom(b.seed);
  const home: number[] = [];
  const corner: number[] = [];
  const seeds: number[] = [];
  const info: number[] = [];
  const index: number[] = [];
  // Three.js axes: a flock's centre and its perch line (game y is −z).
  const centre = (f: Flock): { readonly x: number; readonly z: number } =>
    f.spots !== undefined ? { x: f.spots.reduce((a, p) => a + p.x, 0) / f.spots.length, z: f.spots.reduce((a, p) => a - p.y, 0) / f.spots.length } : { x: f.x ?? 0, z: -(f.y ?? 0) };
  const state = flocks.slice(0, maxFlocks).map((f, i): FlockState => ({
    id: f.id,
    index: i,
    ...centre(f),
    radius: f.radius,
    // Ground flocks leave the paving at dusk; perched ones already roost.
    roosts: f.perch === true,
    takeoffs: 0,
    takeoff: -1e4,
    landing: -1e4,
    quietSince: 0,
    flee: new THREE.Vector2(0, -1),
    status: 'grounded',
  }));
  let quads = 0;
  const quad = (p: readonly number[], kind: number, s: readonly number[], inf: readonly number[]): void => {
    const base = quads * 4;
    for (const [cx, cy] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ] as const) {
      home.push(...p);
      corner.push(cx, cy, kind);
      seeds.push(...s);
      info.push(...inf);
    }
    index.push(base, base + 1, base + 2, base, base + 2, base + 3);
    quads += 1;
  };
  const birds: { readonly p: readonly number[]; readonly s: readonly number[]; readonly inf: readonly number[] }[] = [];
  flocks.slice(0, maxFlocks).forEach((f, fi) => {
    const c = centre(f);
    const perch = f.perch === true;
    const along = { x: f.along?.x ?? 1, z: -(f.along?.y ?? 0) };
    const count = f.count ?? 0;
    const spread = f.spread ?? 0;
    const spots: (readonly number[])[] =
      f.spots !== undefined
        ? f.spots.map((p) => [p.x, p.h, -p.y])
        : Array.from({ length: count }, (_, i) => {
            // Blue-noise-ish scatter: a jittered sunflower spiral.
            const a = i * 2.39996 + random() * 0.6;
            const r = perch ? 0 : spread * Math.sqrt((i + 0.5) / count);
            const t = perch ? ((i + 0.5) / count - 0.5) * 2 * spread + (random() - 0.5) * 0.12 : 0;
            const x = perch ? c.x + along.x * t : c.x + Math.cos(a) * r;
            const z = perch ? c.z + along.z * t : c.z + Math.sin(a) * r * 0.8;
            return [x, f.h ?? groundAt(x, z), z];
          });
    spots.forEach((p, i) => {
      const gull = f.species === 'gull';
      // Mostly blue-bar, a few dark chequers and one pale bird per flock.
      const row = gull ? 3 : i % 5 === 3 ? 1 : i % 7 === 5 ? 2 : 0;
      birds.push({ p, s: [random(), random(), random(), row], inf: [fi, perch ? 1 : 0, i % 3 === 2 ? 0 : 1, gull ? 1 : 0] });
    });
  });
  // Shadows first so the sprites always draw over them within the batch.
  for (const bird of birds) quad(bird.p, 0, bird.s, bird.inf);
  for (const bird of birds) quad(bird.p, 1, bird.s, bird.inf);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(home, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(new Float32Array(home.length), 3));
  geometry.setAttribute('corner', new THREE.Float32BufferAttribute(corner, 3));
  geometry.setAttribute('seed', new THREE.Float32BufferAttribute(seeds, 4));
  geometry.setAttribute('info', new THREE.Float32BufferAttribute(info, 4));
  geometry.setIndex(index);
  const flockUniform = Array.from({ length: maxFlocks }, () => new THREE.Vector4(-1e4, -1e4, 0, -1));
  const uniforms = { time: { value: 0 }, flocks: { value: flockUniform }, reduced: { value: 0 }, lowQuality: { value: 0 } };
  const texture = birdAtlas();
  // A lit, shadow-receiving material (like the characters) whose vertex stage is the flock evaluator.
  const material = new THREE.MeshStandardMaterial({
    map: texture,
    emissive: '#ffffff',
    emissiveMap: texture,
    emissiveIntensity: b.emissive,
    roughness: 1,
    metalness: 0,
    transparent: true,
    depthWrite: true,
    side: THREE.DoubleSide,
    // Transparent double-sided materials otherwise draw twice (back, front).
    forceSinglePass: true,
  });
  addShaderHook(material, {
    key: 'scene-birds-v2',
    uniforms,
    vertex: [
      { chunk: 'common', after: birdCommon(birdCell * birdPixel) },
      { chunk: 'uv_vertex', before: 'birdEval();', after: 'vMapUv=bUv;vEmissiveMapUv=bUv;' },
      // Cards face the camera like the character sprites; blobs face up.
      { chunk: 'beginnormal_vertex', after: 'objectNormal=vBirdKind>.5?vec3(.29,.12,.95):vec3(0.,1.,0.);' },
      { chunk: 'begin_vertex', after: 'transformed=bTransformed;' },
      // A camera-facing card, nudged forward so rails do not clip the feet.
      { chunk: 'project_vertex', after: 'if(vBirdKind>.5){mvPosition.xy+=bOffset;mvPosition.z+=.12;gl_Position=projectionMatrix*mvPosition;}' },
    ],
    fragment: [
      { chunk: 'common', after: 'varying vec2 vBirdUv;varying float vBirdKind,vBirdAlpha;' },
      { chunk: 'alphatest_fragment', after: 'if(vBirdKind>.5&&(diffuseColor.a<.5||vBirdAlpha<.5))discard;diffuseColor.a=1.;' },
      { chunk: 'opaque_fragment', after: 'if(vBirdKind<.5){float blob=(1.-smoothstep(.5,1.,length(vBirdUv)))*.3*vBirdAlpha;if(blob<.01)discard;gl_FragColor=vec4(vec3(.05,.04,.1),blob);}' },
    ],
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'birds';
  mesh.frustumCulled = false;
  mesh.renderOrder = 1;
  mesh.receiveShadow = true;
  mesh.userData['dynamic'] = true;
  mesh.visible = false;
  let time = 0;
  const takeoff = (s: FlockState, fromX: number, fromZ: number): void => {
    const f = flockUniform[s.index];
    if (f === undefined) return;
    s.flee.set(s.x - fromX, s.z - fromZ);
    if (s.flee.lengthSq() < 1e-4) s.flee.set(0.3, -1);
    s.flee.normalize();
    // Mostly up-screen (−z), but every flock sometimes breaks sideways or toward the camera.
    const bias = b.fleeBias[(s.index + s.takeoffs * 3) % b.fleeBias.length] ?? [0, -0.6];
    s.takeoffs += 1;
    s.flee.x += bias[0];
    s.flee.y += bias[1];
    if (s.flee.lengthSq() < 1e-4) s.flee.set(0.3, -1);
    s.flee.normalize();
    f.z = s.flee.x;
    f.w = s.flee.y;
    f.x = time;
    s.takeoff = time;
    s.status = 'flying';
    s.quietSince = time;
  };
  return {
    mesh,
    count: birds.length,
    setActive(active) {
      mesh.visible = active && birds.length > 0;
    },
    update(dt, actors, { enabled, night }) {
      uniforms.reduced.value = enabled ? 0 : 1;
      // The character-style self-light, which dusk swallows.
      material.emissiveIntensity = b.emissive - b.emissiveNight * night;
      if (!enabled) {
        // Still (reduced motion): everyone home in a frozen idle pose.
        for (const s of state) {
          const gone = night > 0.5 && !s.roosts;
          s.takeoff = gone ? -1e3 : -1e4;
          s.landing = -1e4;
          s.status = gone ? 'roosting' : 'grounded';
          flockUniform[s.index]?.set(s.takeoff, -1e4, 0, -1);
        }
        return;
      }
      time += dt;
      uniforms.time.value = time;
      for (const s of state) {
        let near = Infinity;
        let who: BirdActor | null = null;
        for (const a of actors) {
          const d = Math.hypot(a.x - s.x, a.z - s.z) - (a.radius ?? s.radius);
          if (d < near) {
            near = d;
            who = a;
          }
        }
        const f = flockUniform[s.index];
        if (f === undefined) continue;
        const flying = f.x > f.y;
        // Stay away while anyone lingers a little beyond the scare radius.
        if (near < b.linger) s.quietSince = time;
        const dusk = night > 0.5 && !s.roosts;
        if (!flying && near < 0 && who !== null) takeoff(s, who.x, who.z);
        // At dusk the ground flocks lift off (quietly) to roost out of sight.
        else if (!flying && dusk) takeoff(s, s.x, s.z + 1.5);
        else if (flying && dusk) {
          if (time - f.x > b.away) s.status = 'roosting';
        } else if (flying && time - s.quietSince > b.quiet) {
          f.y = time;
          s.landing = time;
          s.status = 'landing';
        } else if (!flying && s.status === 'landing' && time - f.y > b.settle) s.status = 'grounded';
        else if (flying && time - f.x > b.away) s.status = 'away';
      }
    },
    flocks: () => state.map((s) => ({ id: s.id, status: s.status })),
    release() {
      geometry.dispose();
      material.dispose();
      texture.dispose();
    },
    dispose() {
      mesh.removeFromParent();
      geometry.dispose();
      material.dispose();
      texture.dispose();
    },
  };
};
